import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdFitnessCenter, MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import TableRowIconActions from '../../components/shared/TableRowIconActions';
import {
  ColumnFilterHeader,
  cellValue,
  matchSelectFilter,
  uniqueColumnOptions,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import TrainerForm from '../admin/trainers/TrainerForm';
import {
  fetchTrainers,
  saveTrainer,
  deleteTrainer,
  syncReceptionistBranchFromProfile,
} from '../../services/receptionist/receptionistApi';
import { usePanelToast } from '../../context/PanelToastContext';
import { instantLoadState } from '../../utils/instantLoad';
import { dedupePeople } from '../../utils/dedupeList';
import {
  getReceptionistBranch,
  RECEPTIONIST_BRANCH_EVENT,
} from '../../services/gym/branchContext';
import {
  exportTrainerCsv,
  exportTrainerExcel,
  exportTrainerPdf,
  exportTrainers,
  printTrainerList,
} from './exportTrainers';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/memberships/memberships.css';
import '../admin/trainers/trainers.css';
import '../admin/adminListPages.css';
import './members/members.css';

function getTrainerStatusLabel(status) {
  return status === 'active' ? 'Active' : 'Inactive';
}

function Trainer() {
  const toast = usePanelToast();
  const trainerBootstrap = instantLoadState('trainers');
  const [list, setList] = useState(trainerBootstrap.initialData);
  const [loading, setLoading] = useState(trainerBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [editingId, setEditingId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [assignedBranch, setAssignedBranch] = useState(() => getReceptionistBranch());
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: 'all',
    specialization: 'all',
    branch: 'all',
    phone: 'all',
    members: 'all',
    status: 'all',
  });

  const loadList = useCallback(async () => {
    try {
      setError('');
      if (!list.length) setLoading(true);
      setList(dedupePeople(await fetchTrainers()));
    } catch (err) {
      setError(err.message || 'Failed to load trainers');
    } finally {
      setLoading(false);
    }
  }, [list.length]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    syncReceptionistBranchFromProfile()
      .then((profile) => {
        setAssignedBranch(profile?.branch || getReceptionistBranch());
      })
      .catch(() => {
        setAssignedBranch(getReceptionistBranch());
      });
  }, []);

  useEffect(() => {
    const syncBranch = () => setAssignedBranch(getReceptionistBranch());
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, syncBranch);
    return () => window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, syncBranch);
  }, []);

  const filteredList = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matched = list.filter((t) => {
      if (query) {
        const matchesSearch = (
          t.name.toLowerCase().includes(query) ||
          t.specialization.toLowerCase().includes(query) ||
          t.branch.toLowerCase().includes(query) ||
          t.phone.includes(query) ||
          t.email.toLowerCase().includes(query) ||
          t.status.toLowerCase().includes(query)
        );
        if (!matchesSearch) return false;
      }
      return (
        matchSelectFilter(cellValue(t.name), filters.name)
        && matchSelectFilter(cellValue(t.specialization), filters.specialization)
        && matchSelectFilter(cellValue(t.branch), filters.branch)
        && matchSelectFilter(cellValue(t.phone), filters.phone)
        && matchSelectFilter(String((t.assignedMembers || []).length), filters.members)
        && matchSelectFilter(getTrainerStatusLabel(t.status), filters.status)
      );
    });
    return dedupePeople(matched);
  }, [list, search, filters]);

  const openModal = () => {
    setFormKey((k) => k + 1);
    setEditingId('');
    setShowModal(true);
  };

  const openEditModal = (trainer) => {
    setFormKey((k) => k + 1);
    setEditingId(trainer.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId('');
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      await deleteTrainer(deleteTarget.id);
      setDeleteTarget(null);
      toast.showDeleted('Trainer');
      await loadList();
    } catch (err) {
      toast.showError(err.message || 'Failed to delete trainer');
    } finally {
      setDeleting(false);
    }
  };

  const getEditInitialData = () => {
    if (!editingId) return undefined;
    const trainer = list.find((t) => t.id === editingId);
    if (!trainer) return undefined;
    return {
      name: trainer.name,
      email: trainer.email,
      phone: trainer.phone,
      specialization: trainer.specialization,
      branch: trainer.branch,
      salary: trainer.salary,
      status: trainer.status,
      certifications: (trainer.certifications || []).join('\n'),
    };
  };

  const getFormInitialData = () => {
    if (editingId) return getEditInitialData();
    return { branch: assignedBranch };
  };

  const handleSaveTrainer = async (form) => {
    const branch = assignedBranch || form.branch;
    if (!branch) {
      setError('No branch assigned. Ask your administrator to set your branch in Receptionist Management.');
      return;
    }
    const record = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      specialization: form.specialization,
      branch,
      salary: form.salary,
      status: form.status,
      certifications: form.certifications,
    };

    try {
      if (editingId) {
        const updated = await saveTrainer(record, editingId);
        setList((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
        toast.showUpdated('Trainer');
      } else {
        const created = await saveTrainer(record);
        setList((prev) => [created, ...prev]);
        toast.showAdded('Trainer');
      }
      closeModal();
    } catch (err) {
      const message = err.message || 'Failed to save trainer';
      setError(message);
      toast.showError(message);
    }
  };

  return (
    <div>
      <PageHeader title="Trainer" subtitle="Add and manage gym trainers" variant="banner">
        <button type="button" className="owner-btn owner-btn-primary" onClick={openModal}>
          <MdFitnessCenter style={{ marginRight: 8 }} />
          Add Trainer
        </button>
      </PageHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading trainers...</p>}

      <div className="rec-member-toolbar">
        <div className="rec-member-toolbar-left">
          <div className="rec-member-search-field">
            <MdSearch className="rec-member-search-icon" />
            <input
              type="text"
              className="owner-form-input rec-member-search-input"
              placeholder="Search trainers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printTrainerList(filteredList)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportTrainerCsv(filteredList)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportTrainerPdf(filteredList)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportTrainerExcel(filteredList)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportTrainers(filteredList)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => t.name)}
                  allLabel="All Names"
                />
                <ColumnFilterHeader
                  label="Specialization"
                  filterKey="specialization"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => t.specialization)}
                  allLabel="All Specializations"
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => t.branch)}
                  allLabel="All Branches"
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => t.phone)}
                  allLabel="All Phones"
                />
                <ColumnFilterHeader
                  label="Members"
                  filterKey="members"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => String((t.assignedMembers || []).length))}
                  allLabel="All Counts"
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (t) => getTrainerStatusLabel(t.status))}
                  allLabel="All Statuses"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length > 0 ? (
                filteredList.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.specialization}</td>
                    <td>{t.branch}</td>
                    <td>{t.phone}</td>
                    <td>{(t.assignedMembers || []).length}</td>
                    <td>
                      <span className={`owner-badge ${t.status === 'active' ? 'owner-badge-success' : 'owner-badge-warning'}`}>
                        {t.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <TableRowIconActions
                        onEdit={() => openEditModal(t)}
                        onDelete={() => setDeleteTarget(t)}
                        editLabel={`Edit ${t.name}`}
                        deleteLabel={`Delete ${t.name}`}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="owner-placeholder-text">No trainers match your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Trainer</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete trainer <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="trainer-form-title"
          >
            <div className="subscription-modal-header">
              <h2 id="trainer-form-title" className="subscription-modal-title">
                {editingId ? 'Edit Trainer' : 'Add Trainer'}
              </h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <TrainerForm
              key={formKey}
              initialData={getFormInitialData()}
              branchReadOnly
              submitLabel={editingId ? 'Save Changes' : 'Add Trainer'}
              onSubmit={handleSaveTrainer}
              onCancel={closeModal}
            />
          </div>
        </div>
      )}

    </div>
  );
}

export default Trainer;
